import Tooltip from '@mui/material/Tooltip';
import ArrowIosDownIcon from 'icons/monochrome/arrow-ios-down.svg';
import { Fragment, useMemo, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Logo } from 'src/components/logo';
import { HEADER, NAV } from 'src/layouts/config-layout';
import { useBoolean } from 'src/hooks/use-boolean';

import CourseSvg from 'icons/monochrome/course.svg';
import StudentSvg from 'icons/monochrome/student.svg';
import SubmissionSvg from 'icons/monochrome/submission.svg';
import FeedbackSvg from 'icons/monochrome/feedback.svg';
import ExamSvg from 'icons/monochrome/exam-submission.svg';
import MembersSvg from 'icons/monochrome/members.svg';
import AnalyticsSvg from 'icons/monochrome/analytics.svg';
import SettingsSvg from 'icons/monochrome/settings.svg';
import MenuSvg from 'icons/monochrome/menu.svg';
import SearchSvg from 'icons/monochrome/search.svg';
import ChatSvg from 'icons/monochrome/chatbot.svg';

import { ProtoNav, ProtoNavContext, ProtoNavGroup } from './proto-nav';

// ----------------------------------------------------------------------
// Dev-only. A faithful, data-free copy of DashboardLayout's chrome so design
// prototypes render WITHOUT auth (the real DashboardLayout depends on auth,
// platform, subscription and posthog hooks). The feature content rendered in
// the <Outlet /> uses 100% real components — only this frame is a stand-in.
//
// A prototype may replace the sidebar with its own navigation via
// useProtoNav (./proto-nav) — its items then switch `?page=`.
// ----------------------------------------------------------------------

const NAV_GROUPS: ProtoNavGroup[] = [
  {
    subheader: 'Management',
    items: [
      { title: 'Courses', icon: CourseSvg },
      { title: 'Students', icon: StudentSvg },
      { title: 'Submissions', icon: SubmissionSvg },
      { title: 'Feedbacks', icon: FeedbackSvg },
      { title: 'Exam submissions', icon: ExamSvg },
      { title: 'Members', icon: MembersSvg },
      { title: 'Analytics', icon: AnalyticsSvg },
      { title: 'Chats', icon: ChatSvg },
    ],
  },
  {
    subheader: 'Others',
    items: [{ title: 'Settings', icon: SettingsSvg }],
  },
];

function NavItem({
  title,
  icon: Icon,
  active,
  muted,
  subItems,
  onClick,
}: {
  title: string;
  icon?: ProtoNavGroup["items"][number]["icon"];
  active?: boolean;
  muted?: boolean;
  subItems?: string[];
  onClick?: () => void;
}) {
  const leading = Icon ? (
    <Icon />
  ) : (
    <Box
      aria-hidden
      sx={{ width: 24, display: 'flex', justifyContent: 'center', flexShrink: 0 }}
    >
      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor' }} />
    </Box>
  );
  const chevron = subItems?.length ? (
    <Tooltip title={subItems.join(' · ')} placement="right">
      <Box component={ArrowIosDownIcon} aria-label={subItems.join(', ')} sx={{ ml: 'auto', width: '16px !important', height: '16px !important' }} />
    </Tooltip>
  ) : null;
  const sx = {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 2,
    width: 'calc(100% - 24px)',
    px: 1.5,
    py: 1.25,
    mx: 1.5,
    borderRadius: 1,
    textAlign: 'left',
    color: active ? 'primary.main' : muted ? 'text.disabled' : 'text.secondary',
    bgcolor: 'transparent',
    typography: 'body1',
    fontWeight: active ? 600 : 500,
    '& svg': { width: 24, height: 24, flexShrink: 0 },
  } as const;

  if (!onClick) {
    return (
      <Box sx={{ ...sx, cursor: 'default' }}>
        {leading}
        <Box
          component="span"
          title={title}
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {title}
        </Box>
        {chevron}
      </Box>
    );
  }

  return (
    <ButtonBase
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        ...sx,
        bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, active ? 0.12 : 0.04) },
      }}
    >
      {leading}
      <Box
          component="span"
          title={title}
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {title}
        </Box>
      {chevron}
    </ButtonBase>
  );
}

export function PrototypeShell() {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [protoNav, setProtoNav] = useState<ProtoNav | null>(null);
  const { value: drawerOpen, onTrue: openDrawer, onFalse: closeDrawer } = useBoolean();
  const ctx = useMemo(() => ({ setNav: setProtoNav }), []);

  // Highlight "Courses" by default (the app's root); the gallery/index keep it neutral.
  // The "group-chat" prototype highlights "Chats" instead, since that's its own nav item.
  const isFeature = location.pathname.includes('/_design/p/');
  const isChatFeature = location.pathname.includes('/_design/p/group-chat');
  const currentPage = params.get('page') ?? protoNav?.defaultPage;

  const groups = protoNav?.groups ?? NAV_GROUPS;

  const isActive = (title: string, pages?: string[]) => {
    if (protoNav) return !!pages && !!currentPage && pages.includes(currentPage);
    return isChatFeature ? title === 'Chats' : isFeature && title === 'Courses';
  };

  const openPage = (pages?: string[]) =>
    protoNav && pages?.length
      ? () => {
          setParams({ page: pages[0] });
          closeDrawer();
          window.scrollTo(0, 0);
        }
      : undefined;

  const navContent = (
    <>
      <Logo sx={{ mt: 3, ml: 4, mb: 2 }} fullIcon height="35px" width="auto" />

      <Box component="nav" sx={{ flexGrow: 1, overflowY: 'auto', pb: 3 }}>
        {groups.map((group) => (
          <Box key={group.subheader} sx={{ mb: 1 }}>
            <Typography
              variant="overline"
              sx={{ display: 'block', px: 3, py: 1.5, color: 'text.disabled' }}
            >
              {group.subheader}
            </Typography>
            {group.items.map((item) => (
              <Fragment key={item.title}>
                <NavItem
                  title={item.title}
                  icon={item.icon}
                  muted={item.muted}
                  subItems={item.children}
                  active={isActive(item.title, item.pages)}
                  onClick={openPage(item.pages)}
                />
                {item.items?.map((sub) => (
                  <NavItem
                    key={sub.title}
                    title={sub.title}
                    muted={sub.muted}
                    subItems={sub.children}
                    active={isActive(sub.title, sub.pages)}
                    onClick={openPage(sub.pages)}
                  />
                ))}
              </Fragment>
            ))}
          </Box>
        ))}
      </Box>
    </>
  );

  const sidebar = (
    <Stack
      sx={{
        height: 1,
        position: 'fixed',
        top: 0,
        left: 0,
        width: NAV.W_VERTICAL,
        bgcolor: 'background.paper',
        borderRight: `dashed 1px ${theme.palette.divider}`,
        zIndex: theme.zIndex.appBar + 2,
      }}
    >
      {navContent}
    </Stack>
  );

  return (
    <ProtoNavContext.Provider value={ctx}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {lgUp && sidebar}

        {!lgUp && (
          <Drawer open={drawerOpen} onClose={closeDrawer} slotProps={{ paper: { sx: { width: NAV.W_VERTICAL } } }}>
            {navContent}
          </Drawer>
        )}

        {/* Header */}
        <AppBar
          position="fixed"
          sx={{
            height: lgUp ? HEADER.H_DESKTOP : HEADER.H_MOBILE,
            width: lgUp ? `calc(100% - ${NAV.W_VERTICAL}px)` : '100%',
            left: lgUp ? NAV.W_VERTICAL : 0,
            boxShadow: 'none',
            bgcolor: alpha(theme.palette.background.default, 0.9),
            backdropFilter: 'blur(6px)',
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ height: 1, px: { xs: 2, lg: 5 } }}>
            {!lgUp && (
              <IconButton sx={{ mr: 1, color: 'text.primary' }} onClick={openDrawer} aria-label="Open menu">
                <MenuSvg width={24} />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton sx={{ color: 'text.primary' }} aria-label="Search">
                <SearchSvg width={22} />
              </IconButton>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
                MA
              </Avatar>
            </Stack>
          </Toolbar>
        </AppBar>

        {/* Main */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: lgUp ? `calc(100% - ${NAV.W_VERTICAL}px)` : '100%',
            ml: lgUp ? `${NAV.W_VERTICAL}px` : 0,
            minHeight: '100vh',
            bgcolor: 'background.contrast',
            pt: `${(lgUp ? HEADER.H_DESKTOP : HEADER.H_MOBILE) + 24}px`,
            px: { xs: 2, lg: 5 },
            pb: 10,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </ProtoNavContext.Provider>
  );
}
